import { useCallback, useMemo, useState } from "react";
import {
  Anchor,
  Badge,
  Center,
  Group,
  NumberFormatter,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { Link } from "react-router-dom";
import {
  getLastMonthRange,
  ReportDateRangePicker,
  type DateRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportExportButton } from "../../components/reports/ReportExportButton";
import { useActivePractices } from "../../hooks/admin/usePractices";
import { usePayablesReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import {
  reportsService,
  type PayableCalculationStatus,
  type PayablesParams,
} from "../../services/reports.service";
import { canonicalReportDateRangeParams } from "./reportDateRangeParams";

const DEFAULT_RANGE: DateRange = getLastMonthRange();

const STATUS_BADGE: Record<
  PayableCalculationStatus,
  { color: string; label: string }
> = {
  calculated: { color: "green", label: "Calculated" },
  pending_confirmation: {
    color: "yellow",
    label: "Terms pending confirmation",
  },
  no_agreement: { color: "gray", label: "No agreement configured" },
};

export function buildPayablesParams(
  dateRange: DateRange,
  practiceId: string | null
): PayablesParams {
  return {
    ...canonicalReportDateRangeParams(dateRange),
    practiceId: practiceId ?? undefined,
  };
}

export function PayablesReportContent() {
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [practiceId, setPracticeId] = useState<string | null>(null);

  const params = useMemo(
    () => buildPayablesParams(dateRange, practiceId),
    [dateRange, practiceId]
  );
  const handleExport = useCallback(
    () => reportsService.exportPayables(params),
    [params]
  );

  const { data, isLoading } = usePayablesReport(params);
  const { data: practices = [], isLoading: isLoadingPractices } =
    useActivePractices();

  const practiceOptions = useMemo(
    () =>
      [...practices]
        .sort((first, second) => first.name.localeCompare(second.name))
        .map((practice) => ({
          value: practice.id,
          label: practice.name,
        })),
    [practices]
  );

  return (
    <Stack gap="lg">
      <Paper p="sm" withBorder bg="yellow.0" maw={650}>
        <Text size="sm">
          To configure payment agreement terms for referring labs, visit{" "}
          <Anchor component={Link} to="/admin/lab-payment-agreements" size="sm">
            Lab Payment Agreements
          </Anchor>{" "}
          in the Administration section.
        </Text>
      </Paper>

      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <Select
              label="Practice"
              placeholder={
                isLoadingPractices ? "Loading practices…" : "All practices"
              }
              data={practiceOptions}
              value={practiceId}
              onChange={setPracticeId}
              searchable
              clearable
              disabled={isLoadingPractices}
              nothingFoundMessage="No practices found"
              w={240}
            />
            <ReportDateRangePicker value={dateRange} onChange={setDateRange} />
          </Group>
          <ReportExportButton
            filename={`payables-${labToday}.csv`}
            fetchData={handleExport}
          />
        </Group>
      </Paper>

      {isLoading ? (
        <Stack gap="xs">
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} h={80} />
          ))}
        </Stack>
      ) : data?.data.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No payables data for the selected period.</Text>
        </Center>
      ) : (
        <Stack gap="md">
          {data?.data.map((lab) => {
            const statusInfo = STATUS_BADGE[lab.calculationStatus];
            return (
              <Paper key={lab.practiceId} withBorder>
                <Group p="md" justify="space-between">
                  <Stack gap={2}>
                    <Text fw={600}>{lab.practiceName}</Text>
                    <Badge variant="light" size="sm">
                      {lab.calculationBasis.replace("_", " ")}
                    </Badge>
                  </Stack>
                  <Stack align="flex-end" gap={2}>
                    <Badge color={statusInfo.color} variant="light">
                      {statusInfo.label}
                    </Badge>
                    {lab.totalOwed != null && (
                      <Text fw={700} c="green">
                        <NumberFormatter
                          value={lab.totalOwed}
                          prefix="$"
                          decimalScale={2}
                          thousandSeparator
                        />
                      </Text>
                    )}
                  </Stack>
                </Group>

                {lab.lineItems.length > 0 && (
                  <Table
                    withTableBorder={false}
                    style={{
                      borderTop: "1px solid var(--mantine-color-gray-2)",
                    }}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Item</Table.Th>
                        <Table.Th>Quantity</Table.Th>
                        <Table.Th>Rate</Table.Th>
                        <Table.Th>Subtotal</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {lab.lineItems.map((item, index) => (
                        <Table.Tr key={index}>
                          <Table.Td>{item.label}</Table.Td>
                          <Table.Td>{item.quantity}</Table.Td>
                          <Table.Td>
                            {item.rate != null ? (
                              <NumberFormatter
                                value={item.rate}
                                prefix="$"
                                decimalScale={2}
                              />
                            ) : (
                              <Text c="dimmed" size="xs">
                                pending
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            {item.subtotal != null ? (
                              <NumberFormatter
                                value={item.subtotal}
                                prefix="$"
                                decimalScale={2}
                              />
                            ) : (
                              <Text c="dimmed" size="xs">
                                pending
                              </Text>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
